import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  useTheme,
  alpha,
  Container,
  Fade,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Assignment as ProjectsIcon,
  CheckCircle as TasksIcon,
  AccessTime as TimeIcon,
  Coffee as BreaksIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { WaveLoading } from '../components/Loader';
import PageHeader from '../components/PageHeader';

const ServiceManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [services, setServices] = useState([]);
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [saving, setSaving] = useState(false);

  // Services-CRUD state (tab 1: manage services list)
  const [tab, setTab] = useState(0);
  const [allServices, setAllServices] = useState([]);
  const [loadingAllServices, setLoadingAllServices] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  // Permission setup source: 'default' = built-in template, '' = blank, or a service Id to copy from
  const [newServiceSetup, setNewServiceSetup] = useState('default');
  const [quickSetupOpen, setQuickSetupOpen] = useState(false);
  const [quickSetupSource, setQuickSetupSource] = useState('default');
  const [applyingSetup, setApplyingSetup] = useState(false);
  const [addingService, setAddingService] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [rowSaving, setRowSaving] = useState(false);
  

  // Map module names to icons
  const getModuleIcon = (moduleName) => {
    const name = moduleName.toLowerCase();
    if (name.includes('project')) return <ProjectsIcon />;
    if (name.includes('task')) return <TasksIcon />;
    if (name.includes('time') || name.includes('transaction')) return <TimeIcon />;
    if (name.includes('break') || name.includes('leisure')) return <BreaksIcon />;
    return <ProjectsIcon />;
  };

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
    fetchAllServices();
    fetchModules();
    fetchRoles();
  }, []);

  // Fetch module status when service changes
  useEffect(() => {
    if (selectedService) {
      fetchModuleStatus(selectedService);
    }
  }, [selectedService]);

  // Fetch permissions and actions when service or module changes
  useEffect(() => {
    if (selectedService && editingModule) {
      fetchPermissions(selectedService, editingModule.id);
      fetchActions(editingModule.id);
    }
  }, [selectedService, editingModule]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch services');
      }

      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error('Fetch services error:', err);
      setError('Failed to fetch services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ------- Services CRUD (list all, add, rename, toggle active) -------
  const fetchAllServices = async () => {
    try {
      setLoadingAllServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch services');
      const data = await res.json();
      setAllServices(data);
    } catch (err) {
      console.error('Fetch all services error:', err);
      setError('Failed to load services list.');
    } finally {
      setLoadingAllServices(false);
    }
  };

  // Calls POST /services/:id/apply-setup. source: 'default' or a service Id to copy from.
  const applyServiceSetup = async (serviceId, source) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services/${serviceId}/apply-setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify(source === 'default' ? {} : { fromServiceId: Number(source) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Failed to apply permission setup.');
    return data;
  };

  const handleQuickSetup = async () => {
    if (!selectedService) return;
    setApplyingSetup(true);
    setError('');
    try {
      const r = await applyServiceSetup(selectedService, quickSetupSource);
      setQuickSetupOpen(false);
      await fetchModuleStatus(selectedService);
      setSuccess(`Setup applied: ${r.modulesEnabled} module(s) enabled, ${r.permissionsGranted} permission(s) granted.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingSetup(false);
    }
  };

  const handleAddService = async () => {
    const name = newServiceName.trim();
    if (!name) {
      setError('Service name is required.');
      return;
    }
    setAddingService(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Service: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to create service.');
      setAllServices((prev) => [...prev, data].sort((a, b) => a.Service.localeCompare(b.Service)));
      setServices((prev) => data.Active ? [...prev, data].sort((a, b) => a.Service.localeCompare(b.Service)) : prev);
      let setupNote = '';
      if (newServiceSetup) {
        try {
          const r = await applyServiceSetup(data.Id, newServiceSetup);
          setupNote = ` ${r.modulesEnabled} module(s) enabled and ${r.permissionsGranted} permission(s) set up.`;
        } catch (setupErr) {
          setupNote = ' Permission setup failed — use Quick setup on the Modules & Permissions tab.';
        }
      }
      setNewServiceName('');
      setNewServiceSetup('default');
      setAddServiceOpen(false);
      setSuccess(`Service "${data.Service}" created.${setupNote}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to create service.');
    } finally {
      setAddingService(false);
    }
  };

  const handleSaveRename = async (id) => {
    const name = renameValue.trim();
    if (!name) {
      setError('Service name is required.');
      return;
    }
    setRowSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Service: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to rename service.');
      setAllServices((prev) => prev.map((s) => s.Id === id ? { ...s, ...data } : s));
      setServices((prev) => prev.map((s) => s.Id === id ? { ...s, ...data } : s));
      setRenameId(null);
      setRenameValue('');
      setSuccess('Service renamed.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to rename service.');
    } finally {
      setRowSaving(false);
    }
  };

  const handleToggleServiceActive = async (svc) => {
    setRowSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const nextActive = !svc.Active;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services/${svc.Id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Active: nextActive }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update service.');
      setAllServices((prev) => prev.map((s) => s.Id === svc.Id ? { ...s, ...data } : s));
      // Keep the active-only `services` list (used by dropdown) in sync
      setServices((prev) => {
        const others = prev.filter((s) => s.Id !== svc.Id);
        return nextActive
          ? [...others, data].sort((a, b) => a.Service.localeCompare(b.Service))
          : others;
      });
      setSuccess(
        `Service ${nextActive ? 'activated' : 'deactivated'}.` +
        (data.setupApplied
          ? ` Default permissions applied (${data.setupApplied.modulesEnabled} module(s), ${data.setupApplied.permissionsGranted} permission(s)).`
          : '')
      );
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to update service.');
    } finally {
      setRowSaving(false);
    }
  };
  // ------- end Services CRUD -------

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch modules');
      }

      const data = await res.json();
      const modulesWithIcons = data.map(module => ({
        id: module.ID,
        name: module.ModuleName,
        isActive: false,
        icon: getModuleIcon(module.ModuleName)
      }));
      setModules(modulesWithIcons);
    } catch (err) {
      console.error('Fetch modules error:', err);
      setError('Failed to fetch modules. Please try again.');
    }
  };

  const fetchModuleStatus = async (serviceId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/modules/status/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch module status');
      }

      const data = await res.json();
      const statusMap = {};
      data.forEach(item => {
        statusMap[item.Moduleid] = item.Active === 1;
      });

      // Update modules with status from ServiceModuleMapping
      setModules(prevModules => 
        prevModules.map(module => ({
          ...module,
          isActive: statusMap[module.id] || false
        }))
      );
    } catch (err) {
      console.error('Fetch module status error:', err);
      // Reset all to inactive on error
      setModules(prevModules => 
        prevModules.map(module => ({ ...module, isActive: false }))
      );
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await res.json();
      const rolesFormatted = data.map(role => ({
        value: role.ID,
        label: role.RolesName
      }));
      setRoles(rolesFormatted);
    } catch (err) {
      console.error('Fetch roles error:', err);
      setError('Failed to fetch roles. Please try again.');
    }
  };

  const fetchActions = async (moduleId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/actions/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch actions: ${res.status} ${errorText}`);
      }

      const data = await res.json();
      setActions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fetch actions error:', err);
      setError('Failed to fetch actions. Please try again.');
      setActions([]);
    }
  };

  const fetchPermissions = async (serviceId, moduleId) => {
    try {
      setLoadingPermissions(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/service-management/permissions/${serviceId}/${moduleId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const data = await res.json();
      
      // Create permissions map: { "roleId_permissionId": status }
      const permissionsMap = {};
      data.forEach((perm) => {
        const key = `${perm.RoleId}_${perm.PermissionId}`;
        permissionsMap[key] = (perm.Status === true || perm.Status === 1);
      });

      setPermissions(permissionsMap);
    } catch (err) {
      console.error('Fetch permissions error:', err);
      setError('Failed to fetch permissions. Please try again.');
      setPermissions({});
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
  };

  const handleEditClick = (module) => {
    setEditingModule(module);
    setModalOpen(true);
  };

  const handleToggleModuleStatus = async (module) => {
    if (!selectedService) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/modules/${selectedService}/${module.id}/toggle`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update module status');
      }

      const data = await res.json();
      
      // Update local state with new Active status from ServiceModuleMapping
      setModules(prevModules => 
        prevModules.map(m => 
          m.id === module.id ? { ...m, isActive: data.Active === 1 } : m
        )
      );
      setSuccess('Module status updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Toggle module status error:', err);
      setError(err.message || 'Failed to update module status. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingModule(null);
    setPermissions({});
    setActions([]);
    setError('');
    setSuccess('');
  };


  const handlePermissionChange = async (roleId, permissionId) => {
    if (!selectedService || !editingModule) return;
    
    const key = `${roleId}_${permissionId}`;
    const currentStatus = permissions[key] || false;
    const newStatus = !currentStatus;
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/service-management/permissions/${selectedService}/${editingModule.id}/toggle`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roleId: parseInt(roleId),
            permissionId: parseInt(permissionId),
            status: newStatus
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update permission');
      }

      // Update local state
      setPermissions(prev => ({
        ...prev,
        [key]: newStatus
      }));
      
      // Show success message
      setSuccess('Permission updated successfully!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Update permission error:', err);
      setError(err.message || 'Failed to update permission. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  const selectedServiceData = services.find((s) => s.Id === parseInt(selectedService));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <PageHeader title="Service Management" subtitle="Configure permissions for services and modules" />

      {error && (
        <Fade in={!!error}>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in={!!success}>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          if (v === 0 && allServices.length === 0) fetchAllServices();
        }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Services" />
        <Tab label="Modules & Permissions" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setNewServiceName(''); setAddServiceOpen(true); }}
            >
              Add Service
            </Button>
          </Box>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {loadingAllServices ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <WaveLoading theme={theme} size="large" />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.05),
                    }}>
                      <TableCell sx={{ fontWeight: 700, width: 80 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Service Name</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 160 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 220 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allServices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No services yet. Click "Add Service" to create one.
                        </TableCell>
                      </TableRow>
                    ) : allServices.map((svc) => (
                      <TableRow key={svc.Id} hover>
                        <TableCell sx={{ color: 'text.secondary', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{svc.Id}</TableCell>
                        <TableCell>
                          {renameId === svc.Id ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                size="small"
                                autoFocus
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveRename(svc.Id);
                                  if (e.key === 'Escape') { setRenameId(null); setRenameValue(''); }
                                }}
                                disabled={rowSaving}
                                sx={{ minWidth: 260 }}
                                inputProps={{ maxLength: 255 }}
                              />
                              <IconButton size="small" onClick={() => handleSaveRename(svc.Id)} disabled={rowSaving} sx={{ color: 'success.main' }}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => { setRenameId(null); setRenameValue(''); }} disabled={rowSaving} sx={{ color: 'text.secondary' }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography sx={{ fontWeight: 500 }}>{svc.Service}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1,
                              py: 0.4,
                              bgcolor: svc.Active ? '#dcfce7' : '#f3f4f6',
                              color: svc.Active ? '#15803d' : '#6b7280',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              borderRadius: 1,
                            }}
                          >
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: svc.Active ? '#10b981' : '#9ca3af' }} />
                            {svc.Active ? 'Active' : 'Deactivated'}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => { setRenameId(svc.Id); setRenameValue(svc.Service); }}
                              disabled={renameId === svc.Id || rowSaving}
                              startIcon={<EditIcon fontSize="small" />}
                              sx={{ color: 'text.secondary' }}
                            >
                              Rename
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleToggleServiceActive(svc)}
                              disabled={rowSaving}
                              sx={{ color: svc.Active ? 'error.main' : 'success.main' }}
                            >
                              {svc.Active ? 'Deactivate' : 'Activate'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}

      {tab === 1 && (<>
      {/* Service Selection */}
      {loading ? (
        <Box
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fafbfc',
            border: '1px solid #e5e7eb',
            borderRadius: 2.5,
          }}
        >
          <WaveLoading theme={theme} size="large" />
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fafbfc',
            border: '1px solid #e5e7eb',
            borderRadius: 2.5,
          }}
        >
          <FormControl sx={{ width: '400px' }} size="small">
            <InputLabel id="service-select-label">
              Select Service
            </InputLabel>
            <Select
              labelId="service-select-label"
              id="service-select"
              value={selectedService}
              label="Select Service"
              onChange={handleServiceChange}
              disabled={loading}
            >
              {services.map((service) => (
                <MenuItem key={service.Id} value={service.Id}>
                  <Typography sx={{ fontWeight: 500 }}>{service.Service}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedService && (
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 1.5, height: 40, textTransform: 'none' }}
              onClick={() => { setQuickSetupSource('default'); setQuickSetupOpen(true); }}
            >
              Quick setup…
            </Button>
          )}
        </Box>
      )}

      {/* Modules Table */}
      {selectedService && (
        <Paper
          elevation={0}
          sx={{
            background: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[1],
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', py: 2 }}>Module Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', py: 2 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', py: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((module) => (
                  <TableRow
                    key={module.id}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, py: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {React.cloneElement(module.icon, { sx: { fontSize: 22, color: '#1976d2' } })}
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {module.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2.5 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
                        <Box
                          role="switch"
                          aria-checked={module.isActive}
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleModuleStatus(module);
                          }}
                          sx={{
                            position: 'relative',
                            width: 26,
                            height: 15,
                            borderRadius: 8,
                            bgcolor: module.isActive ? '#10b981' : '#d1d5db',
                            cursor: 'pointer',
                            transition: 'background-color 0.18s ease',
                            flexShrink: 0,
                            '&:hover': { filter: 'brightness(0.95)' },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              left: module.isActive ? 13 : 2,
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              bgcolor: '#ffffff',
                              transition: 'left 0.18s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                            }}
                          />
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.4,
                            bgcolor: module.isActive ? '#dcfce7' : '#f3f4f6',
                            color: module.isActive ? '#15803d' : '#6b7280',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 1,
                            letterSpacing: 0,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: module.isActive ? '#10b981' : '#9ca3af',
                              flexShrink: 0,
                            }}
                          />
                          {module.isActive ? 'Active' : 'Inactive'}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(module)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty State */}
      {!selectedService && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: alpha('#1976d2', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Select a service to manage modules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a service from the dropdown above to configure module permissions
          </Typography>
        </Box>
      )}
      </>)}

      {/* Quick setup dialog — apply defaults or copy another service's permissions */}
      <Dialog open={quickSetupOpen} onClose={() => !applyingSetup && setQuickSetupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Quick setup — {services.find((s) => s.Id === parseInt(selectedService, 10))?.Service}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Apply</InputLabel>
            <Select label="Apply" value={quickSetupSource} onChange={(e) => setQuickSetupSource(e.target.value)} disabled={applyingSetup}>
              <MenuItem value="default">Default setup</MenuItem>
              {services.filter((s) => s.Id !== parseInt(selectedService, 10)).map((s) => (
                <MenuItem key={s.Id} value={String(s.Id)}>Copy from {s.Service}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Turns on the needed modules and adds permissions for each role. Nothing you've already set is removed —
            you can fine-tune individual roles afterwards.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQuickSetupOpen(false)} disabled={applyingSetup} color="inherit">Cancel</Button>
          <Button onClick={handleQuickSetup} disabled={applyingSetup} variant="contained">
            {applyingSetup ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addServiceOpen} onClose={() => !addingService && setAddServiceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Service</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Service Name"
            value={newServiceName}
            onChange={(e) => setNewServiceName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !addingService) handleAddService(); }}
            disabled={addingService}
            inputProps={{ maxLength: 255 }}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Permissions</InputLabel>
            <Select
              label="Permissions"
              value={newServiceSetup}
              onChange={(e) => setNewServiceSetup(e.target.value)}
              disabled={addingService}
            >
              <MenuItem value="default">Default setup (recommended)</MenuItem>
              {allServices.filter((s) => s.Active).map((s) => (
                <MenuItem key={s.Id} value={String(s.Id)}>Copy from {s.Service}</MenuItem>
              ))}
              <MenuItem value="">Blank — I'll set them up myself</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddServiceOpen(false)} disabled={addingService} color="inherit">Cancel</Button>
          <Button onClick={handleAddService} disabled={addingService || !newServiceName.trim()} variant="contained">
            {addingService ? 'Creating…' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[24],
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            background: alpha('#1976d2', 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {editingModule && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                {React.cloneElement(editingModule.icon, { sx: { fontSize: 20 } })}
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {editingModule?.name} Permissions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure role permissions for this module
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {loadingPermissions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <WaveLoading theme={theme} size="large" />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', py: 2 }}>Role</TableCell>
                    {actions.map((action) => (
                      <TableCell key={action.Id} align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', py: 2 }}>
                        {action.ActionName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => {
                    const roleLabel = role.label;
                    return (
                      <TableRow
                        key={role.value}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, py: 2.5 }}>{roleLabel}</TableCell>
                        {actions.map((action) => {
                          const key = `${role.value}_${action.Id}`;
                          const isChecked = permissions[key] || false;
                          return (
                            <TableCell key={action.Id} align="center" sx={{ py: 2.5 }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handlePermissionChange(role.value, action.Id)}
                                disabled={saving}
                                sx={{
                                  color: '#1976d2',
                                  '&.Mui-checked': {
                                    color: '#1976d2',
                                  },
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            gap: 2,
          }}
        >
          <Button
            onClick={handleCloseModal}
            disabled={saving}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};

export default ServiceManagement;
